import { useState, useRef, useEffect, useCallback } from "react";

import { ExtensionSearchResult } from "lib/api";
import { searchExtensions } from "lib/api";

const useSearchResults = (searchText: string) => {
  const [searchResults, setSearchResults] = useState<ExtensionSearchResult[]>([]);
  const [searchTextForSearchResults, setSearchTextForSearchResults] = useState("");
  const [totalResults, setTotalResults] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const abortControllerRef = useRef<AbortController>();
  const [page, setPage] = useState(1);

  const loadMore = useCallback(() => {
    setPage((page) => page + 1);
    setLoadingMore(true);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  useEffect(() => {
    async function asyncSearchExtensions() {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        if (searchText.length === 0) {
          setLoading(false);
          setTotalResults(0);
          setSearchTextForSearchResults("");
          setLoadingMore(false);
          setSearchResults([]);
          return;
        }

        setLoading(true);

        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        const response = await searchExtensions({ searchText, page, options: { signal } });

        setLoading(false);
        setLoadingMore(false);
        setSearchTextForSearchResults(searchText);
        if (page > 1) {
          setSearchResults((searchResults) => [...searchResults, ...response.data]);
        } else {
          setSearchResults(response.data);
        }
        setTotalResults(response.total_results);
      } catch {
        // Catch abort errors
      }
    }

    asyncSearchExtensions();
  }, [searchText, setLoading, page]);

  return {
    searchResults,
    searchTextForSearchResults,
    loading,
    totalResults,
    loadMore,
    hasMore: searchResults && totalResults && searchResults.length < totalResults,
    loadingMore,
  };
};

export default useSearchResults;
